import { useSyncExternalStore } from 'react';

/**
 * «Il dispositivo dichiara di non avere rete.»
 *
 * `useSyncExternalStore` e non la coppia `useState` + `useEffect`, per una ragione
 * che e' il cuore del difetto da riparare: lo stato iniziale viene letto DURANTE il
 * primo render, non dopo. Con un effetto, chi apre la PWA senza rete vedrebbe un
 * frame di app «normale» prima che l'avviso compaia — cioe' proprio il lampo di
 * schermata sana che fa sembrare un guasto quello che succede subito dopo.
 *
 * Nessun rimbalzo (debounce), a differenza di `ConnessionePersaBanner`, che ne ha
 * due secondi: li' si guarda un socket, che in riconnessione sfarfalla davvero;
 * qui si guarda il sistema operativo, che non lo fa. E ritardare il caso «apro
 * l'app e la rete non c'e'» sarebbe il difetto, non la cura.
 *
 * ATTENZIONE AL LIMITE, che e' la meta' meno ovvia: `navigator.onLine === true`
 * significa «attaccato a UNA rete», non «internet raggiungibile». Un captive
 * portal d'albergo, il server giu', il DNS rotto: per il browser sono tutti
 * «online». Questo hook copre quindi la sola assenza DICHIARATA, ed e'
 * volutamente cieco all'altra meta'.
 *
 * L'altra meta' e' gia' coperta, e da un meccanismo diverso: una richiesta che
 * fallisce diventa `NETWORK_ERROR_CODE` in `services/api.ts`, che
 * `AvvisoSommario` e `chiaveErroreCaricamento` traducono. I due avvisi sono
 * complementari — «il dispositivo dice di essere scollegato» e «la chiamata non
 * e' arrivata» — e non vanno fusi in uno: il primo si sa senza chiedere niente a
 * nessuno, il secondo solo dopo aver provato.
 */

function sottoscrivi(notifica: () => void): () => void {
  window.addEventListener('online', notifica);
  window.addEventListener('offline', notifica);
  return () => {
    window.removeEventListener('online', notifica);
    window.removeEventListener('offline', notifica);
  };
}

function leggi(): boolean {
  // `navigator.onLine` e' opzionale in teoria (e assente in qualche ambiente di
  // test): l'assenza di informazione non e' «senza rete», quindi si sta zitti.
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Il valore lato server / prerender: nessun avviso. */
function leggiSulServer(): boolean {
  return false;
}

export function useSenzaRete(): boolean {
  return useSyncExternalStore(sottoscrivi, leggi, leggiSulServer);
}
